import { useEffect, useState } from 'react';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useSearchParams } from 'react-router';
import { motion } from 'framer-motion';
import { CustomModal } from 'components/customModal';
import { useGetInvitationDetails } from 'api/invite/hooks';
import { Logo } from 'assets/landing';
import {
	CardTopPattern,
	CardMartiniGlass,
	CardLogo
} from 'assets';
import Styles from './invitationModal.module.scss';

const cardVariants = {
	initial: (isMobile) => ({
		x: 0,
		y: isMobile ? 100 : 0,
	}),
	animate: (isMobile) => ({
		x: isMobile ? 0 : -250,
		y: isMobile ? 0 : 0,
		transition: {
			duration: 0.9,
			ease: 'easeInOut',
			delay: 1,
		},
	}),
};

const detailsVariants = {
	initial: (isMobile) => ({
		opacity: 0,
		x: isMobile ? 0 : -100,
		y: 0
	}),
	animate: (isMobile) => ({
		opacity: 1,
		x: 0,
		y: isMobile ? 600 : 0,
		transition: {
			duration: 0.8,
			ease: 'easeOut',
			delay: 1.3,
		},
	}),
};

const backgroundVariants = {
	initial: { backgroundColor: 'rgba(0,0,0,0)' },
	animate: {
		backgroundColor: 'rgba(0,0,0,1)',
		transition: {
			duration: 1,
			ease: 'easeInOut',
			delay: 1,
		},
	},
};

export const InvitationModal = ({ show, onClose }) => {
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const isMobile = useMediaQuery({ maxWidth: 991 });
	const [backdropClass, setBackdropClass] = useState(Styles.backdropDark);

	const restaurantID = searchParams.get('restaurantID');
	const campaignID = searchParams.get('campaignID');
	const campaignInviteID = searchParams.get('campaignInviteID');
	const firstName = searchParams.get('firstName');
	const lastName = searchParams.get('lastName');

	const { data: invitationDetails, isPending } = useGetInvitationDetails({
		restaurantID,
		campaignID,
		campaignInviteID
	});

	const inviteeUserID = invitationDetails?.campaign_invite?.invitee_user_id;

	if (inviteeUserID) {
		localStorage.setItem('invitee_user_id', inviteeUserID);
	}

	const goToInvitation = () => {
		navigate({
			pathname: '/invitation',
			search: `?${searchParams.toString()}`
		});
	};

	useEffect(() => {
		if (!show) return;

		const timer = setTimeout(() => {
			setBackdropClass(Styles.backdropLight);
		}, 1000);

		// eslint-disable-next-line consistent-return
		return () => { clearTimeout(timer); };
	}, [show]);

	return (
		<CustomModal
			show={show}
			onClose={onClose}
			dialogStyles={Styles.modalContainer}
			bodyStyles={Styles.modalBody}
			backdropStyles={show && backdropClass}
		>
			<motion.div
				className={Styles.invitationContainer}
				variants={backgroundVariants}
				initial="initial"
				animate="animate"
			>
				<motion.div
					className={Styles.cardContainer}
					variants={cardVariants}
					custom={isMobile}
					initial="initial"
					animate="animate"
				>
					<div className={Styles.card}>
						<CardTopPattern className={Styles.cardTopPattern} />
						<div className={Styles.headingContainer}>
							<h6>Inaugral Speakeasy Masquerade Ball</h6>
							<span className='d-flex gap-2'>
								<h4>You are invited,{' '}</h4>
								<h4 className='text-capitalize'>{firstName} {lastName}</h4>
							</span>
							<p>January 31st, 2026 at 7pm</p>
						</div>
						<div className={Styles.venueTextContainer}>
							<div className='d-flex gap-4'>
								<div className='d-flex flex-column gap-3'>
									<div className='d-flex flex-column text-start'>
										<p>Speakeasy Restaurant Emaar Karachi</p>
										<p>January 31st, 2026 at 7pm</p>
									</div>
									<p>RSVP is required | Dress code mandatory</p>
								</div>
								<img
									className={Styles.qrCode}
									src={invitationDetails?.campaign_invite?.qr_code}
									alt='qr-code'
								/>
							</div>
						</div>
						<p className={Styles.bottomText}>
							Join us for the launch of Speakeasy and for a preview of<br />The Black Jacket
						</p>

						<div className={Styles.cardLogoContainer}>
							<CardLogo className={Styles.cardLogo} />
							<CardMartiniGlass className={Styles.cardGlass} />
						</div>
						<CardTopPattern className={Styles.cardBottomPattern} />
					</div>
				</motion.div>
				<motion.div
					className={Styles.details}
					variants={detailsVariants}
					custom={isMobile}
					initial="initial"
					animate="animate"
				>
					<div className="d-flex flex-column align-items-center gap-3">
						<h1>A Private Invitation</h1>
						<h5>Unlock Your Entry To The Elite Only Masquerade Party At</h5>
						<img className={Styles.logo} src={Logo} alt="speakeasy-logo" />
					</div>

					<div className="d-flex flex-column align-items-center gap-md-5 gap-3">
						<h6>Claim Your Disguise</h6>
						<button
							disabled={isPending || !invitationDetails || !inviteeUserID}
							onClick={goToInvitation}
							className={Styles.ctaButton}
						>
							Reserve My Mask
						</button>
					</div>
				</motion.div>
			</motion.div>
		</CustomModal>
	);
};
